/* eslint-disable quotes */
import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import {
  Button,
  Pane,
  Accordion,
  AccordionSet,
  Checkbox,
  Loading,
  ConfirmationModal,
  Row,
  Col,
  InfoPopover,
  KeyValue,
  Icon,
  Select,
  MultiColumnList,
} from '@folio/stripes/components';
import { useOkapiKy, useCallout, useStripes } from '@folio/stripes/core';

import './ReindexManagement.css';
import ProgressBar from './components/ProgressBar';
import './components/ProgressBar.css';

const ReindexManagement = () => {
  const intl = useIntl();
  const ky = useOkapiKy();
  const callout = useCallout();
  const stripes = useStripes();

  // Check permissions
  const hasReindexPermission =
    stripes?.hasPerm?.('inventory-storage.instance.reindex.collection.get') ||
    false;
  const hasReindexCreatePermission =
    stripes?.hasPerm?.('inventory-storage.instance.reindex.post') ||
    false;
  const hasReindexDeletePermission =
    stripes?.hasPerm?.('inventory-storage.instance.reindex.item.delete') ||
    false;


  const [reindexJobs, setReindexJobs] = useState([]);
  const [activeJob, setActiveJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [showConfirmStart, setShowConfirmStart] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);

  const [reindexOptions, setReindexOptions] = useState({
    resourceName: 'instance',
    recreateIndex: false,
    indexSettings: {
      numberOfShards: 1,
      numberOfReplicas: 0,
      refreshInterval: -1,
    },
  });

  const statusPollingInterval = 5000; // 5 seconds
  const maxProgressEstimate = 100000; // Estimated max records for progress calculation

  // Fetch reindex jobs
  const fetchReindexJobs = useCallback(async () => {
    try {
      const response = await ky.get('instance-storage/reindex').json();
      const jobs = response.reindexJobs || [];

      setReindexJobs(jobs);

      // Find active job
      const active = jobs.find(
        (job) => job.jobStatus === 'In progress' || job.jobStatus === 'Pending cancel',
      );
      setActiveJob(active);
    } catch (error) {
      console.error('Reindex fetch error:', error);

      if (callout) {
        let errorMessage =
          'ui-plugin-inventory-templates.reindexManagement.fetchError';

        // Check for permission errors
        if (error.message && error.message.includes('permission')) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        } else if (error.status === 403) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        } else if (error.status === 401) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.authError';
        }

        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({ id: errorMessage }),
        });
      }
    }
  }, [ky, callout, intl]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);

      // Only fetch if user has permission
      if (hasReindexPermission) {
        await fetchReindexJobs();
      }

      setLoading(false);
    };
    loadData();
  }, [hasReindexPermission]); // Add permission dependency

  // Polling for active jobs
  useEffect(() => {
    let interval;
    if (activeJob && hasReindexPermission) {
      interval = setInterval(() => {
        fetchReindexJobs();
      }, statusPollingInterval);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeJob, hasReindexPermission]); // Add permission dependency

  const startReindexJob = async () => {
    if (!hasReindexCreatePermission) {
      if (callout) {
        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.reindexManagement.permissionError',
          }),
        });
      }
      return;
    }

    try {
      setStarting(true);

      const requestBody = {
        resourceName: reindexOptions.resourceName,
        recreateIndex: reindexOptions.recreateIndex,
        ...(reindexOptions.recreateIndex && {
          indexSettings: reindexOptions.indexSettings,
        }),
      };


      const response = await ky
        .post('instance-storage/reindex', {
          json: requestBody,
          timeout: 30000, // 30 seconds timeout
        })
        .json();


      setActiveJob(response);
      setShowConfirmStart(false);

      if (callout) {
        callout.sendCallout({
          type: 'success',
          message: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.reindexManagement.jobStarted',
          }),
        });
      }

      // Refresh job list
      await fetchReindexJobs();
    } catch (error) {
      console.error('Reindex start error:', error);

      if (callout) {
        let errorMessage =
          'ui-plugin-inventory-templates.reindexManagement.startError';

        if (error.message && error.message.includes('permission')) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        } else if (error.status === 403) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        } else if (error.status === 404) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.endpointNotFound';
        }

        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({ id: errorMessage }),
        });
      }
    } finally {
      setStarting(false);
    }
  };

  const cancelReindexJob = async () => {
    if (!hasReindexDeletePermission) {
      if (callout) {
        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.reindexManagement.permissionError',
          }),
        });
      }
      return;
    }

    try {
      await ky.delete(`instance-storage/reindex/${selectedJobId}`);

      setShowConfirmCancel(false);
      setSelectedJobId(null);

      if (callout) {
        callout.sendCallout({
          type: 'success',
          message: intl.formatMessage({
            id: 'ui-plugin-inventory-templates.reindexManagement.jobCancelled',
          }),
        });
      }

      // Refresh job list
      await fetchReindexJobs();
    } catch (error) {
      console.error('Reindex cancel error:', error);

      if (callout) {
        let errorMessage =
          'ui-plugin-inventory-templates.reindexManagement.cancelError';

        if (error.message && error.message.includes('permission')) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        } else if (error.status === 403) {
          errorMessage =
            'ui-plugin-inventory-templates.reindexManagement.permissionError';
        }

        callout.sendCallout({
          type: 'error',
          message: intl.formatMessage({ id: errorMessage }),
        });
      }
    }
  };

  const getJobStatusIcon = (status) => {
    switch (status) {
      case 'In progress':
        return 'clock';
      case 'Ids published':
        return 'check-circle';
      case 'Id publishing failed':
        return 'exclamation-circle';
      case 'Pending cancel':
        return 'ban';
      case 'Id publishing cancelled':
        return 'times-circle';
      default:
        return 'question-circle';
    }
  };

  const getJobStatusColor = (status) => {
    switch (status) {
      case 'In progress':
        return '#17a2b8';
      case 'Ids published':
        return '#28a745';
      case 'Id publishing failed':
        return '#dc3545';
      case 'Pending cancel':
        return '#ffc107';
      case 'Id publishing cancelled':
        return '#6c757d';
      default:
        return '#6c757d';
    }
  };

  const calculateProgress = (job) => {
    if (!job || job.jobStatus === 'Ids published') return 100;
    if (
      job.jobStatus === 'Id publishing failed' ||
      job.jobStatus === 'Id publishing cancelled'
    ) return 0;

    // Estimate progress based on published count
    const progressPercent = Math.min(
      ((job.published || 0) / maxProgressEstimate) * 100,
      95,
    );
    return Math.round(progressPercent);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Intl.DateTimeFormat(intl.locale, {
      dateStyle: 'short',
      timeStyle: 'medium',
    }).format(new Date(dateString));
  };

  const jobsListFormatter = {
    submittedDate: (job) => formatDateTime(job.submittedDate),
    jobStatus: (job) => (
      <span style={{ color: getJobStatusColor(job.jobStatus) }}>
        <Icon icon={getJobStatusIcon(job.jobStatus)} />{' '}
        <FormattedMessage
          id={`ui-inventory.reindexManagement.status.${job.jobStatus.replace(/\s+/g, '_').toLowerCase()}`}
          defaultMessage={job.jobStatus}
        />
      </span>
    ),
    published: (job) => (job.published || 0).toLocaleString(),
    progress: (job) => (
      <ProgressBar
        value={calculateProgress(job)}
        className="reindex-progress-cell"
      />
    ),
    actions: (job) => (job.jobStatus === 'In progress' || job.jobStatus === 'Pending cancel' ? (
      <Button
        buttonStyle="danger"
        size="small"
        onClick={() => {
          setSelectedJobId(job.id);
          setShowConfirmCancel(true);
        }}
        disabled={
            job.jobStatus === 'Pending cancel' || !hasReindexDeletePermission
          }
      >
        <FormattedMessage id="ui-plugin-inventory-templates.cancel" />
      </Button>
    ) : null),
  };

  const jobsColumnMapping = {
    submittedDate: (
      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.columns.submittedDate" />
    ),
    jobStatus: (
      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.columns.status" />
    ),
    published: (
      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.columns.recordsProcessed" />
    ),
    progress: (
      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.columns.progress" />
    ),
    actions: (
      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.columns.actions" />
    ),
  };

  if (loading) {
    return <Loading />;
  }

  // Show permission error if user doesn't have access
  if (!hasReindexPermission) {
    return (
      <Pane
        defaultWidth="fill"
        fluidContentWidth
        paneTitle={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.title" />
        }
      >
        <div className="reindex-management">
          <div className="reindex-card">
            <div className="permission-error">
              <Icon icon="exclamation-triangle" size="large" />
              <h3>
                <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.permissionError" />
              </h3>
              <p>
                <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.permissionDetails" />
              </p>
            </div>
          </div>
        </div>
      </Pane>
    );
  }

  return (
    <Pane
      defaultWidth="fill"
      fluidContentWidth
      paneTitle={
        <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.title" />
      }
    >
      <div className="reindex-management">
        <div className="reindex-card">
          <div className="reindex-header">
            <Icon icon="refresh" size="medium" />
            <h3>
              <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.configuration" />
            </h3>
          </div>

          <AccordionSet>
            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.currentStatus" />
              }
              id="reindex-status"
            >
              {activeJob ? (
                <Row>
                  <Col xs={12}>
                    <div className="active-job-status">
                      <Icon
                        icon={getJobStatusIcon(activeJob.jobStatus)}
                        size="large"
                      />
                      <div className="active-job-details">
                        <h4>
                          <FormattedMessage
                            id={`ui-inventory.reindexManagement.status.${activeJob.jobStatus.replace(/\s+/g, '_').toLowerCase()}`}
                            defaultMessage={activeJob.jobStatus}
                          />
                        </h4>
                        <p>
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.reindexManagement.recordsProcessed"
                            values={{
                              count: (
                                activeJob.published || 0
                              ).toLocaleString(),
                            }}
                          />
                        </p>
                        <ProgressBar value={calculateProgress(activeJob)} />
                        <p className="job-started">
                          <FormattedMessage
                            id="ui-plugin-inventory-templates.reindexManagement.startedAt"
                            values={{
                              time: formatDateTime(activeJob.submittedDate),
                            }}
                          />
                        </p>
                      </div>
                    </div>
                  </Col>
                </Row>
              ) : (
                <Row>
                  <Col xs={12}>
                    <div className="no-active-job">
                      <Icon icon="info-circle" />
                      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.noActiveJob" />
                    </div>
                  </Col>
                </Row>
              )}
            </Accordion>

            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.newReindex" />
              }
              id="new-reindex"
            >
              <Row>
                <Col xs={6}>
                  <Select
                    label={
                      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.resourceType" />
                    }
                    value={reindexOptions.resourceName}
                    onChange={(e) => setReindexOptions({
                      ...reindexOptions,
                      resourceName: e.target.value,
                    })
                    }
                    disabled={!!activeJob || !hasReindexCreatePermission}
                  >
                    <option value="instance">
                      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.resourceType.instance" />
                    </option>
                  </Select>
                </Col>
                <Col xs={6}>
                  <div className="reindex-options">
                    <Checkbox
                      label={
                        <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.recreateIndex" />
                      }
                      checked={reindexOptions.recreateIndex}
                      onChange={(e) => setReindexOptions({
                        ...reindexOptions,
                        recreateIndex: e.target.checked,
                      })
                      }
                      disabled={!!activeJob || !hasReindexCreatePermission}
                    />
                    <InfoPopover
                      content={
                        <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.recreateIndexTooltip" />
                      }
                    />
                  </div>
                </Col>
              </Row>

              <Row>
                <Col xs={12}>
                  <div className="reindex-actions">
                    <Button
                      buttonStyle="primary"
                      onClick={() => setShowConfirmStart(true)}
                      disabled={
                        !!activeJob || starting || !hasReindexCreatePermission
                      }
                      loading={starting}
                    >
                      <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.startReindex" />
                    </Button>
                    {activeJob && (
                      <p className="reindex-disabled-message">
                        <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.jobInProgress" />
                      </p>
                    )}
                    {!hasReindexCreatePermission && (
                      <p className="reindex-disabled-message">
                        <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.permissionError" />
                      </p>
                    )}
                  </div>
                </Col>
              </Row>
            </Accordion>

            <Accordion
              label={
                <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.jobHistory" />
              }
              id="job-history"
            >
              <Row>
                <Col xs={12}>
                  <MultiColumnList
                    contentData={reindexJobs}
                    formatter={jobsListFormatter}
                    columnMapping={jobsColumnMapping}
                    visibleColumns={[
                      'submittedDate',
                      'jobStatus',
                      'published',
                      'progress',
                      'actions',
                    ]}
                    columnWidths={{
                      submittedDate: '20%',
                      jobStatus: '20%',
                      published: '15%',
                      progress: '25%',
                      actions: '20%',
                    }}
                    interactive={false}
                  />
                </Col>
              </Row>
            </Accordion>
          </AccordionSet>
        </div>
      </div>

      <ConfirmationModal
        open={showConfirmStart}
        heading={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.confirmStart.heading" />
        }
        message={
          <FormattedMessage
            id="ui-plugin-inventory-templates.reindexManagement.confirmStart.message"
            values={{
              resourceType: intl.formatMessage({
                id: `ui-inventory.reindexManagement.resourceType.${reindexOptions.resourceName}`,
              }),
              recreate: reindexOptions.recreateIndex
                ? intl.formatMessage({
                  id: 'ui-plugin-inventory-templates.reindexManagement.withRecreate',
                })
                : '',
            }}
          />
        }
        confirmLabel={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.startReindex" />
        }
        cancelLabel={
          <FormattedMessage id="ui-plugin-inventory-templates.cancel" />
        }
        onConfirm={startReindexJob}
        onCancel={() => setShowConfirmStart(false)}
      />

      <ConfirmationModal
        open={showConfirmCancel}
        heading={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.confirmCancel.heading" />
        }
        message={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.confirmCancel.message" />
        }
        confirmLabel={
          <FormattedMessage id="ui-plugin-inventory-templates.cancel" />
        }
        cancelLabel={
          <FormattedMessage id="ui-plugin-inventory-templates.reindexManagement.keepRunning" />
        }
        onConfirm={cancelReindexJob}
        onCancel={() => {
          setShowConfirmCancel(false);
          setSelectedJobId(null);
        }}
      />
    </Pane>
  );
};

ReindexManagement.propTypes = {};

export default ReindexManagement;
